import React from 'react';
import { AlertTriangle, RotateCcw, ArrowLeft } from 'lucide-react';
import { useT } from '../context/LanguageContext';

/* The app had no error boundary at all, so ONE component throwing while it
   rendered unmounted the entire tree and left a white page — no message, no
   sidebar, nothing to act on and nothing to report. React logs the failure to
   the console and nowhere else, which is only useful to somebody who already
   has DevTools open.

   This catches the throw at the routed screen, keeps the shell around it, and
   puts the error where the person looking at it can read and send it. It is
   NOT a repair: the screen that failed is still broken, and the text says so
   rather than inviting another try at the same thing.

   Class component because getDerivedStateFromError has no hook equivalent. */
class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: '' };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept out of state until here: componentStack is what names the screen
    // that failed, and it only arrives with this second call.
    this.setState({ stack: info?.componentStack || '' });
    // Still logged, so an open console and a copied report say the same thing.
    // eslint-disable-next-line no-console
    console.error('screen crashed:', error, info?.componentStack);
  }

  render() {
    const { error, stack } = this.state;
    const { t, children } = this.props;
    if (!error) return children;

    const detail = [
      `${error.name || 'Error'}: ${error.message || String(error)}`,
      stack ? `\n${stack.trim()}` : '',
    ].join('');

    return (
      <div className="flex min-h-[60vh] items-start justify-center p-6">
        <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
              <AlertTriangle size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-lg font-semibold">{t('error.title')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('error.body')}</p>
            </div>
          </div>

          {/* Open by default: hiding it behind a click is how a report comes
              back saying only "it did not work". */}
          <details open className="mt-4">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('error.details')}
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted/50 p-3 text-[11px] leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {detail}
            </pre>
          </details>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <RotateCcw size={14} /> {t('error.reload')}
            </button>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted/50"
            >
              <ArrowLeft size={14} /> {t('error.back')}
            </button>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(detail)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted/50"
            >
              {t('error.copy')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

/* useT is a hook, so it is read out here and handed down: the boundary itself
   cannot be a function component. This wrapper renders BEFORE anything can
   throw, so the translation is already in hand by the time it is needed. */
export default function ErrorBoundary({ children, resetKey }) {
  const { t } = useT();
  // Remounted per route, so navigating away from a broken screen clears it.
  return <Boundary key={resetKey} t={t}>{children}</Boundary>;
}
